/**
 * Takes an array of project ids and an array of projects and returns the project ids sorted by
 * the sort key of your choice.
 * @param {array} projects - Array of projects including the projects to be sorted.
 * @param {string} sortBy - sort by value
 * @returns {Array} - A new array of project ids sorted by the sort key.
 */

import type { ProjectSortBy } from '@frameio/shared/resources/projects/types';
import { PLACEHOLDER_PROJECT_ID_PREFIX } from './constants';

type SortableProject = {
  id: string;
  name?: string;
  insertedAt?: string;
  archivedAt?: string | null;
};

export const sortWorkspaceProjects = (
  projects: Array<SortableProject>,
  sortBy: ProjectSortBy
) => {
  const { field, order } = sortBy;

  if (field === 'created_at') {
    return [...projects].sort((a, b) =>
      order === 'asc'
        ? sortAsc(a, b, 'insertedAt')
        : sortDesc(a, b, 'insertedAt')
    );
  }

  if (field === 'status') {
    return [...projects].sort((a, b) =>
      order === 'asc'
        ? sortAsc(a, b, 'archivedAt')
        : sortDesc(a, b, 'archivedAt')
    );
  }

  return [...projects].sort((a, b) =>
    order === 'asc' ? sortAsc(a, b, 'name') : sortDesc(a, b, 'name')
  );
};

type SortByField = 'name' | 'insertedAt' | 'archivedAt';

function sortAsc(a: SortableProject, b: SortableProject, field: SortByField) {
  if (
    a.id.includes(PLACEHOLDER_PROJECT_ID_PREFIX) ||
    b.id.includes(PLACEHOLDER_PROJECT_ID_PREFIX) ||
    a[field] === b[field]
  )
    return 0;

  if (field === 'archivedAt') {
    if (a[field] === null) return -1;
    if (b[field] === null) return 1;
  }

  if (a[field] === null || a[field] === undefined) return 1;
  if (b[field] === null || b[field] === undefined) return -1;
  if (typeof field === 'string') {
    return (a[field] as string).localeCompare(b[field] as string);
  }
  return 0;
}

function sortDesc(a: SortableProject, b: SortableProject, field: SortByField) {
  if (
    a.id.includes(PLACEHOLDER_PROJECT_ID_PREFIX) ||
    b.id.includes(PLACEHOLDER_PROJECT_ID_PREFIX) ||
    a[field] === b[field]
  )
    return 0;

  if (field === 'archivedAt') {
    if (a[field] === null) return 1;
    if (b[field] === null) return -1;
  }

  if (a[field] === null || a[field] === undefined) return -1;
  if (b[field] === null || b[field] === undefined) return 1;
  if (typeof field === 'string') {
    return (b[field] as string).localeCompare(a[field] as string);
  }
  return 0;
}
